// File: /components/Shufflemaps/Listings/ListingCard.jsx
import React, { useEffect, useState, useCallback, useRef } from 'react';
import DatabaseImage from '../../Media/DatabaseImage';
import styles from './ListingCard.module.css';
import { getAuth } from 'firebase/auth';

/** Simple in-memory caches to avoid duplicate fetches during a session */
const ratingCache = new Map();   // key: listingId -> { ratingAverage, ratingCount, userRating }
const thumbCache  = new Map();   // key: listingId -> activeThumbnailUrl|null

const ListingCard = ({
  listingId,
  name, // still used for alt text, etc.
  status = '',
  category,
  fallbackRating = 0,
  shortDescription,
  imageUrl,
  activeThumbnailUrl: activeThumbFromProps = null,
  suppressEmptyRatingDisplay = true,
  isFavoriteInitial = false,
  showStatus = true,
  showFavoriteButton = true,
}) => {
  const categoriesFromProp = Array.isArray(category) ? category : category ? [category] : [];
  const categoryStringFromProp = categoriesFromProp.join(', ');

  const [categoryText, setCategoryText] = useState(categoryStringFromProp);
  useEffect(() => { setCategoryText(categoryStringFromProp); }, [categoryStringFromProp]);

  const normalizedStatus = (status || '').toLowerCase();

  // Visibility
  const rootRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  // Rating
  const [ratingAverage, setRatingAverage] = useState(fallbackRating || 0);
  const [ratingCount, setRatingCount] = useState(0);
  const [userRating, setUserRating] = useState(null);
  const [ratingLoading, setRatingLoading] = useState(true);
  const [ratingError, setRatingError] = useState(null);

  // Thumbnail
  const [activeThumbnailUrl, setActiveThumbnailUrl] = useState(activeThumbFromProps);
  const [thumbLoading, setThumbLoading] = useState(!activeThumbFromProps);
  const [thumbError, setThumbError] = useState(null);

  // Favorite
  const [isFavorite, setIsFavorite] = useState(Boolean(isFavoriteInitial));
  const [favBusy, setFavBusy] = useState(false);
  useEffect(() => { setIsFavorite(Boolean(isFavoriteInitial)); }, [isFavoriteInitial]);

  const computeFill = (value) => Math.max(0, Math.min(100, ((value ?? 0) / 5) * 100));

  const withAuthHeaders = async () => {
    const headers = { 'Content-Type': 'application/json' };
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (currentUser) {
        const token = await currentUser.getIdToken(false);
        headers.Authorization = `Bearer ${token}`;
      }
    } catch {}
    return headers;
  };

  const loadRatings = useCallback(async () => {
    if (!listingId) return;

    if (ratingCache.has(listingId)) {
      const cached = ratingCache.get(listingId);
      setRatingAverage(cached.ratingAverage ?? 0);
      setRatingCount(cached.ratingCount ?? 0);
      setUserRating(cached.userRating ?? null);
      setRatingLoading(false);
      return;
    }

    setRatingLoading(true);
    setRatingError(null);
    try {
      const headers = await withAuthHeaders();
      const params = new URLSearchParams({ listingId: String(listingId) });
      const resp = await fetch(`/api/shufflemaps/listings/listing-rating?${params.toString()}`, {
        method: 'GET',
        headers,
        cache: 'no-store',
      });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(`Fetch failed: ${resp.status} ${txt}`);
      }
      const json = await resp.json();
      const next = {
        ratingAverage: json.ratingAverage ?? 0,
        ratingCount: json.ratingCount ?? 0,
        userRating: json.userRating ?? null,
      };
      ratingCache.set(listingId, next);
      setRatingAverage(next.ratingAverage);
      setRatingCount(next.ratingCount);
      setUserRating(next.userRating);
    } catch (err) {
      console.warn('ListingCard loadRatings error', err);
      setRatingError('Could not load rating');
    } finally {
      setRatingLoading(false);
    }
  }, [listingId]);

  const loadActiveThumbnail = useCallback(async () => {
    if (activeThumbFromProps) {
      setActiveThumbnailUrl(activeThumbFromProps);
      setThumbLoading(false);
      thumbCache.set(listingId, activeThumbFromProps);
      return;
    }
    if (!listingId) return;

    if (thumbCache.has(listingId)) {
      setActiveThumbnailUrl(thumbCache.get(listingId));
      setThumbLoading(false);
      return;
    }

    setThumbLoading(true);
    setThumbError(null);
    try {
      const url =
        `/api/shufflemaps/listings/media/${encodeURIComponent(String(listingId))}/thumbnails` +
        `?v=${Date.now()}`;
      const resp = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        cache: 'no-store',
      });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(`Thumb list failed: ${resp.status} ${txt}`);
      }
      const json = await resp.json();
      const thumbs = Array.isArray(json?.thumbnails) ? json.thumbnails : [];
      const active = thumbs.find(t => Number(t.is_active) === 1 || t.is_active === true) || null;
      const file = active?.file_name || null;
      thumbCache.set(listingId, file);
      setActiveThumbnailUrl(file);
    } catch (err) {
      console.warn('ListingCard loadActiveThumbnail error', err);
      setActiveThumbnailUrl(null);
      setThumbError('Could not load thumbnail');
    } finally {
      setThumbLoading(false);
    }
  }, [listingId, activeThumbFromProps]);

  // IntersectionObserver: only load when visible
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return setIsVisible(true);

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setIsVisible(true);
            io.disconnect();
            break;
          }
        }
      },
      { root: null, rootMargin: '200px 0px', threshold: 0.01 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => { if (isVisible) loadRatings(); }, [isVisible, loadRatings]);
  useEffect(() => { if (isVisible) loadActiveThumbnail(); }, [isVisible, loadActiveThumbnail]);
  useEffect(() => { if (activeThumbFromProps) setActiveThumbnailUrl(activeThumbFromProps); }, [activeThumbFromProps]);

  const toggleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!listingId || favBusy) return;

    setFavBusy(true);
    try {
      const resp = await fetch('/api/shufflemaps/favorites/toggle', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ listingId }),
      });

      if (resp.status === 401) {
        alert('Sign in to save favorites.');
        return;
      }
      if (!resp.ok) {
        const t = await resp.text().catch(() => '');
        throw new Error(t || `HTTP ${resp.status}`);
      }

      let next = !isFavorite;
      try {
        const data = await resp.json();
        if (typeof data?.favorited === 'boolean') next = data.favorited;
      } catch {}
      setIsFavorite(next);

      try {
        window.dispatchEvent(new CustomEvent('shufflemaps:favorites-changed', {
          detail: { listingId: Number(listingId), favorited: next }
        }));
      } catch {}
    } catch (err) {
      console.warn('Favorite toggle failed', err);
    } finally {
      setFavBusy(false);
    }
  };

  const hasRatings = ratingCount > 0;
  const starFillPercent = computeFill(hasRatings ? ratingAverage : 0);

  return (
    <div className={styles.card} ref={rootRef}>
      {showFavoriteButton && (
        <button
          type="button"
          className={`${styles.favBtn} ${isFavorite ? styles.favActive : ''} ${favBusy ? styles.favBusy : ''}`}
          aria-label={isFavorite ? 'Remove from favorites' : 'Save to favorites'}
          title={isFavorite ? 'Remove from favorites' : 'Save to favorites'}
          onClick={toggleFavorite}
          data-active={isFavorite ? 'true' : 'false'}
        >
          <span className={styles.favIcon} aria-hidden="true" data-active={isFavorite ? 'true' : 'false'}>
            ♥
          </span>
        </button>
      )}

      {/* ✅ Title REMOVED from body — it will be the accordion header now */}

      {showStatus && (
        <span className={`${styles.status} ${styles[normalizedStatus]}`}>{status}</span>
      )}

      {shortDescription && (
        <div className={styles.description}>{shortDescription}</div>
      )}

      <div className={styles.details}>
        <div className={styles.category}>{categoryText}</div>

        {(!suppressEmptyRatingDisplay || hasRatings) && (
          <div className={styles.rating}>
            <div
              className={styles.starContainer}
              aria-label={
                hasRatings
                  ? `Average rating: ${Number(ratingAverage).toFixed(1)}`
                  : 'No ratings yet'
              }
            >
              <div className={styles.starBackground}>★★★★★</div>
              {hasRatings && (
                <div
                  className={styles.starForeground}
                  style={{ width: `${starFillPercent}%` }}
                >
                  ★★★★★
                </div>
              )}
            </div>

            <div className={styles.ratingValue}>
              {ratingLoading ? (
                <span>Loading…</span>
              ) : hasRatings ? (
                <span>
                  {Number(ratingAverage).toFixed(1)} ({ratingCount})
                </span>
              ) : !suppressEmptyRatingDisplay ? (
                <span>No ratings yet</span>
              ) : null}
            </div>
          </div>
        )}
      </div>

      {userRating != null && !ratingLoading && (
        <div className={styles.userRated}>You rated {Number(userRating).toFixed(1)}</div>
      )}

      {/* Optional media (kept here; accordion will handle lazy-mount if you pass content as a function) */}
      {imageUrl && (
        <DatabaseImage
          image={imageUrl}
          alt={`${name} thumbnail`}
          className={styles.image}
          loading="lazy"
        />
      )}

      {activeThumbnailUrl && (
        <DatabaseImage
          image={activeThumbnailUrl}
          alt={`${name} active thumbnail`}
          className={styles.activeThumbnail}
          loading="lazy"
        />
      )}

      {(ratingError || thumbError || thumbLoading) && (
        <div className={styles.error}>Some info unavailable</div>
      )}
    </div>
  );
};

export default ListingCard;
